import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** Ulashish urinishining natijasi — UI shunga qarab xabar ko'rsatadi. */
export type ReceiptShareResult = 'shared' | 'downloaded' | 'cancelled';

interface ReceiptFile {
  blob: Blob;
  name: string;
}

/**
 * Buyurtma hisobotini PDF qilib yuklab olish va ulashish.
 *
 * Fayl BACKENDDA yasaladi (`GET /api/orders/{id}/pdf`) — A4 jadval ko'rinishi, pul
 * formatlash qoidasi (`Currencies.Decimals`, belgi o'rni) va aralash savatni chek
 * raqami bo'yicha yig'ish mantiqi o'sha yerda yashaydi. Bu yerda faqat yetkazib berish.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptExportService {
  private http = inject(HttpClient);
  private messages = inject(MessageService);
  private translate = inject(TranslateService);

  private readonly base = `${environment.apiUrl}/api/orders`;

  /**
   * Oxirgi olingan fayl. Bitta yozuv yetarli — bir vaqtda faqat bitta chek oynasi
   * ochiq bo'ladi, ro'yxat esa xotirada PDF'larni yig'ib borardi.
   *
   * Kalitda TIL ham bor: hisobot serverda tarjima qilinadi, shuning uchun til
   * almashtirilganda eski nusxa yaroqsiz bo'ladi.
   */
  private cached: { key: string; file: ReceiptFile } | null = null;

  private cacheKey(orderId: string): string {
    return `${orderId}|${this.lang()}`;
  }

  private lang(): string {
    return this.translate.currentLang || 'uz';
  }

  /**
   * Chek oynasi ochilganda chaqiriladi — faylni oldindan olib qo'yadi.
   *
   * <b>Bu ulashish uchun SHART, tezlik uchun emas.</b> `navigator.share()` transient
   * user activation talab qiladi: tugma bosilgandan keyin HTTP javobini kutsak,
   * aktivatsiya tugaydi va brauzer `NotAllowedError` beradi — natijada ulashish
   * o'rniga jimgina yuklab olish bo'lardi. Fayl oldindan tayyor bo'lsa,
   * `navigator.share()` bosish ishlovchisining O'ZIDA sinxron chaqiriladi.
   */
  prefetch(orderId: string): void {
    const key = this.cacheKey(orderId);
    if (this.cached?.key === key) return;
    this.fetchPdf(orderId).subscribe({
      next: (file) => (this.cached = { key, file }),
      // Jim: tugma bosilganda qayta urinib ko'riladi va xato o'shanda ko'rsatiladi.
      error: () => undefined,
    });
  }

  /** Faylni yuklab olish. */
  downloadPdf(orderId: string): Observable<void> {
    return this.file(orderId).pipe(
      map((file) => {
        this.deliver(file);
      }),
      catchError(() => this.fail()),
    );
  }

  /**
   * Faylni ulashish. Ulashish imkoni bo'lmagan muhitda (ko'p desktop brauzerlar fayl
   * ulashishni qo'llab-quvvatlamaydi) fayl yuklab olinadi va foydalanuvchiga NEGA
   * shunday bo'lgani aytiladi — ilgari bu jimgina sodir bo'lib, tugma buzuq
   * ko'rinardi.
   */
  sharePdf(orderId: string): Observable<ReceiptShareResult> {
    return this.file(orderId).pipe(
      switchMap((file) => this.share(file)),
      catchError(() => this.fail()),
    );
  }

  // ── Ichki ──────────────────────────────────────────────────────────────────

  /**
   * Fayl manbai. Keshda bo'lsa `of(...)` SINXRON emit qiladi — shu tufayli
   * `navigator.share()` bosish aktivatsiyasini yo'qotmaydi.
   */
  private file(orderId: string): Observable<ReceiptFile> {
    const key = this.cacheKey(orderId);
    if (this.cached?.key === key) return of(this.cached.file);
    return this.fetchPdf(orderId).pipe(tap((file) => (this.cached = { key, file })));
  }

  private fetchPdf(orderId: string): Observable<ReceiptFile> {
    // Til so'rovda uzatiladi: PDF serverda yasaladi va `ngx-translate` lug'ati u
    // yerga yetib bormaydi. Backend notanish kodni o'zbekchaga tushiradi.
    const params = new HttpParams().set('lang', this.lang());

    return this.http
      .get(`${this.base}/${orderId}/pdf`, { params, responseType: 'blob', observe: 'response' })
      .pipe(
        map((resp) => {
          const disposition = resp.headers.get('Content-Disposition') ?? '';
          const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
          const name = match?.[1]
            ? decodeURIComponent(match[1])
            : `buyurtma_${new Date().toISOString().slice(0, 10)}.pdf`;

          // Tur ATAYLAB majburlanadi: `HttpClient` qaytargan blob'da `type` bo'sh
          // bo'lishi mumkin, bo'sh turli faylni esa `canShare` rad etadi.
          const blob = new Blob([resp.body as Blob], { type: 'application/pdf' });
          return { blob, name };
        }),
      );
  }

  /**
   * Faylni foydalanuvchiga berish. Ilova Flutter WebView ichida ochilgan bo'lsa
   * brauzerning yuklab olishi ishlamaydi — fayl `FileDownloader` ko'prigi orqali
   * native tomonga uzatiladi (`reports/report.service.ts` dagi bilan bir xil sxema).
   */
  private deliver(file: ReceiptFile): void {
    const bridge = window.flutter_inappwebview;
    if (bridge?.callHandler) {
      const reader = new FileReader();
      reader.onloadend = (): void => {
        bridge.callHandler('FileDownloader', {
          base64: reader.result as string,
          name: file.name,
        });
      };
      reader.readAsDataURL(file.blob);
      return;
    }

    const url = URL.createObjectURL(file.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  private share(file: ReceiptFile): Observable<ReceiptShareResult> {
    // Flutter WebView'da `navigator.share` yo'q — native ulashish varag'ini ochish
    // uchun alohida handler kerak.
    //
    // SHARTNOMA: `FileShare` ulashish bo'lganda `true` qaytarishi SHART. Ro'yxatdan
    // o'tmagan handler `null` bilan MUVAFFAQIYATLI yakunlanadi (xato tashlamaydi),
    // shuning uchun faqat `.then` ga ishonib bo'lmaydi — aks holda handler qo'shilmagan
    // ilovada tugma "ulashildi" deb turib, hech nima qilmasdi.
    const bridge = window.flutter_inappwebview;
    if (bridge?.callHandler) {
      const reader = new FileReader();
      return from(
        new Promise<ReceiptShareResult>((resolve) => {
          reader.onloadend = (): void => {
            const payload = { base64: reader.result as string, name: file.name };
            bridge
              .callHandler('FileShare', payload)
              .then((ok) => {
                if (ok === true) {
                  resolve('shared');
                  return;
                }
                resolve(this.fallbackToDownload(file));
              })
              .catch(() => resolve(this.fallbackToDownload(file)));
          };
          reader.readAsDataURL(file.blob);
        }),
      );
    }

    const shareFile = new File([file.blob], file.name, { type: 'application/pdf' });
    const data: ShareData = { files: [shareFile], title: file.name };

    // `canShare` fayl bilan tekshirilishi SHART: desktop Chrome'da `navigator.share`
    // mavjud, lekin fayl ulashishni rad etadi.
    if (!navigator.canShare?.(data)) return of(this.fallbackToDownload(file));

    return from(
      navigator.share(data).then(
        (): ReceiptShareResult => 'shared',
        (err: unknown): ReceiptShareResult => {
          // Foydalanuvchi ulashish varag'ini yopgani xato emas — xabar chiqarilmaydi.
          if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
          return this.fallbackToDownload(file);
        },
      ),
    );
  }

  /** Ulashib bo'lmadi — fayl yuklab olinadi va sabab aytiladi. */
  private fallbackToDownload(file: ReceiptFile): ReceiptShareResult {
    this.deliver(file);
    this.messages.add({
      severity: 'info',
      summary: this.translate.instant('SALES.RECEIPT.SHARE_UNSUPPORTED'),
    });
    return 'downloaded';
  }

  /**
   * Xabar ko'rsatib oqimni JIMGINA tugatadi. Xatoni qayta otsak, chaqiruvchi
   * komponentlarning hech birida `error` ishlovchisi yo'q — brauzer konsolida
   * ushlanmagan xato bo'lib chiqardi.
   */
  private fail(): Observable<never> {
    this.messages.add({
      severity: 'error',
      summary: this.translate.instant('SALES.RECEIPT.EXPORT_FAILED'),
    });
    return EMPTY;
  }
}
